/**
 * DCE Phase Grouping Utility
 *
 * Groups series by DCE phase based on SeriesDescription keywords.
 * A series is identified as a DCE phase if its description contains
 * a recognized phase keyword (Pre, Art, Ven, Del, etc.).
 * Series with the same "base description" (description with phase keyword removed)
 * are grouped together.
 */

export interface ThumbnailItem {
  displaySetInstanceUID: string;
  description?: string;
  seriesTime?: string;
  [key: string]: any;
}

export interface DCEPhaseGroup {
  groupLabel: string;
  phases: Array<ThumbnailItem & { phaseLabel: string; phaseOrder: number }>;
}

export interface GroupedThumbnails {
  dceGroups: DCEPhaseGroup[];
  ungrouped: ThumbnailItem[];
}

// Each entry: [regex to match the phase word(s), phase label, sort order]
const PHASE_PATTERNS: Array<[RegExp, string, number]> = [
  [/\bpre[-\s]?contrast\b/i, 'Pre-contrast', 0],
  [/\bpre\b/i, 'Pre-contrast', 0],
  [/\barter(ial)?\b/i, 'Arterial', 1],
  [/\bart\b/i, 'Arterial', 1],
  [/\bven(ous)?\b/i, 'Venous', 2],
  [/\bven\b/i, 'Venous', 2],
  [/\bdelayed?\b/i, 'Delayed', 3],
  [/\bdel\b/i, 'Delayed', 3],
];

interface PhaseMatch {
  phaseLabel: string;
  phaseOrder: number;
  base: string; // description with phase word removed and trimmed
}

function detectPhase(description: string): PhaseMatch | null {
  for (const [pattern, label, order] of PHASE_PATTERNS) {
    if (pattern.test(description)) {
      const base = description.replace(pattern, '').replace(/\s+/g, ' ').trim();
      return { phaseLabel: label, phaseOrder: order, base };
    }
  }
  return null;
}

export function getDCEPhaseGroups(thumbnails: ThumbnailItem[]): GroupedThumbnails {
  // Map: base description → list of (thumbnail + phase info)
  const groupMap = new Map<string, Array<ThumbnailItem & { phaseLabel: string; phaseOrder: number }>>();

  const phaseMatches = new Map<string, PhaseMatch>(); // displaySetInstanceUID → match

  for (const thumb of thumbnails) {
    const desc = thumb.description?.trim() || '';
    if (!desc) continue;
    const match = detectPhase(desc);
    if (!match) continue;
    phaseMatches.set(thumb.displaySetInstanceUID, match);

    const key = match.base.toUpperCase();
    if (!groupMap.has(key)) groupMap.set(key, []);
    groupMap.get(key)!.push({ ...thumb, phaseLabel: match.phaseLabel, phaseOrder: match.phaseOrder });
  }

  const dceGroups: DCEPhaseGroup[] = [];
  const groupedUIDs = new Set<string>();

  for (const [key, members] of groupMap.entries()) {
    const unique = [...new Map(members.map(t => [t.displaySetInstanceUID, t])).values()];

    // Sort by phase order, then by seriesTime as tiebreaker
    const sorted = [...unique].sort((a, b) => {
      if (a.phaseOrder !== b.phaseOrder) return a.phaseOrder - b.phaseOrder;
      return (a.seriesTime ?? '').localeCompare(b.seriesTime ?? '');
    });

    // Use the base description as the group label (restore original casing from first member)
    const firstMatch = phaseMatches.get(sorted[0].displaySetInstanceUID)!;
    const groupLabel = firstMatch.base || sorted[0].description || key;

    sorted.forEach(item => groupedUIDs.add(item.displaySetInstanceUID));
    dceGroups.push({ groupLabel, phases: sorted });
  }

  // Sort groups: groups with lower minimum phaseOrder come first
  dceGroups.sort((a, b) => Math.min(...a.phases.map(p => p.phaseOrder)) - Math.min(...b.phases.map(p => p.phaseOrder)));

  const ungrouped = thumbnails.filter(t => !groupedUIDs.has(t.displaySetInstanceUID));

  return { dceGroups, ungrouped };
}
